import { exportBackup, type Timestamp } from '@anytracker/core'
import { useCallback, useState } from 'react'

import { useToast } from '../components/Toast.js'
import { useServices } from '../services/ServicesProvider.js'

/**
 * Writes a backup file and records that it happened.
 *
 * One definition for every "Back up now" button, so the rule below cannot be
 * kept on one screen and forgotten on another: the backup is recorded only on
 * a save that actually happened. A cancelled download that reset the clock
 * would leave the app quiet about a backup that never took place — the reason
 * `ExportResult` distinguishes the two.
 */
export function useBackupDownload(
  /** The screen's own `useBackupHealth().markBackedUp`, so its status refreshes. */
  markBackedUp: (at: Timestamp, entryCount: number) => Promise<void>,
): {
  readonly download: () => Promise<void>
  readonly busy: boolean
} {
  const { storage, clock, exporter } = useServices()
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const download = useCallback(async () => {
    setBusy(true)
    try {
      const { file, backup } = await exportBackup(storage, clock)
      const result = await exporter.save(file)
      if (result.status === 'saved') {
        await markBackedUp(backup.exportedAt, backup.counts.entries)
        toast.show({ message: `Saved ${result.filename}` })
      } else if (result.status === 'failed') {
        toast.show({ message: result.reason, tone: 'danger' })
      }
    } finally {
      setBusy(false)
    }
  }, [storage, clock, exporter, markBackedUp, toast])

  return { download, busy }
}
